import { CallMetricFilters } from '../call-metric-filters/call-metric-filters';
import styles from './surveys-header.module.scss';
import RefreshArrow from '@/assets/icons/refresh-ccw-01.svg?react';
import Download1 from '@/assets/icons/download-04.svg?react';
import Search from '@/assets/icons/search-md.svg?react';
import { Card, Flex, Input, Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/button/button';
import Dropdown from '../form/dropdown/dropdown';
import InfoCircle from '@/assets/icons/info-circle.svg?react';
import { useUser } from '@/hooks/use-user/use-user';
import { useDebouncedFilterQuery } from '@/hooks/use-debounced-filter-query/use-debounced-filter-query';
import { useIsBayonne } from '@/hooks/use-is-bayonne/use-is-bayonne';
import { CustomTableHeader } from '../custom-table-header/custom-table-header';
import {
  useConversationTypeOptions,
  useScoreOptions,
  useScoreTypeOptions,
  useSurveyCategoryOptions,
} from './surveys-header.constants.ts';
import Icon from '@ant-design/icons';
import { SurveysFilters } from '@/services/surveys-service/surveys-service.types';
import { Score } from '../score/score';
import clsx from 'clsx';
import { CustomRangePicker } from '../custom-range-picker/custom-range-picker.tsx';
import dayjs, { Dayjs } from 'dayjs';
import { CustomFilters } from '../custom-filters/custom-filters.tsx';
import { useScreenSize } from '@/hooks/use-screen-size/use-screen-size.tsx';

interface SurveysHeaderProps {
  surveysNumber: number;
  companiesList: string[];
  averageScore: number;
  onRefreshData: () => void;
  filters: SurveysFilters;
  onChangeFilters: (filters: SurveysFilters) => void;
  dates: [Dayjs | null, Dayjs | null] | null;
  setDates: (dates: [Dayjs | null, Dayjs | null] | null) => void;
  onDateRangeClear: () => void;
  onChangeDateRangeFilter: (filterValue: {
    time_range: { start_time: string; end_time: string };
  }) => void;
  onSearchQueryChange: (query: string) => void;
  onExportHistory: () => void;
  searchResults?: string[];
  isSearchLoading?: boolean;
  searchError?: Error | null;
}

export const SurveysHeader = ({
  surveysNumber,
  onRefreshData,
  filters,
  onChangeFilters,
  companiesList,
  averageScore,
  dates,
  setDates,
  onDateRangeClear,
  onChangeDateRangeFilter,
  onSearchQueryChange,
  onExportHistory,
  searchResults,
  isSearchLoading,
  searchError,
}: SurveysHeaderProps) => {
  const { t } = useTranslation(['surveys', 'common']);
  const { isFetched } = useUser();
  const { filterValue, onQueryChange } = useDebouncedFilterQuery(filters, onChangeFilters);
  const conversationTypeOptions = useConversationTypeOptions();
  const scoreOptions = useScoreOptions();
  const scoreTypeOptions = useScoreTypeOptions();
  const surveyCategoryOptions = useSurveyCategoryOptions();

  const isBayonne = useIsBayonne();
  const screenSize = useScreenSize();
  const isMobile = screenSize.width < 768;

  const handleConversationTypeChange = (value: string) => {
    onChangeFilters({ ...filters, conversation_type: value });
  };

  const handleScoreChange = (value: number) => {
    onChangeFilters({ ...filters, score: value });
  };

  const handleScoreTypeChange = (value: string) => {
    onChangeFilters({ ...filters, score_type: value });
  };

  const handleSurveyCategoryChange = (value: string) => {
    onChangeFilters({ ...filters, survey_category: value as SurveysFilters['survey_category'] });
  };

  return (
    <Card className={styles.container} style={{ padding: '16px 24px' }}>
      <Flex vertical>
        <Flex wrap="wrap" gap={20} className={styles.topHeaderContainer}>
          <Flex className={styles.surveysCountSectionContainer}>
            <Flex gap={30} align="center">
              <Button
                aria-label={t('surveys:header.refresh')}
                buttonVariant="secondary"
                onClick={onRefreshData}
                icon={<RefreshArrow stroke={styles.iconColor} />}
              ></Button>
              <CustomTableHeader
                textHeader1={t('surveys:header.countLine1')}
                textHeader2={t('surveys:header.countLine2')}
                count={surveysNumber}
              />
            </Flex>

            <Flex className={styles.filtersSectionContainer}>
              {!isBayonne && isFetched && (
                <CallMetricFilters
                  value={{ time_range: filters.time_range }}
                  onChange={(value) => {
                    onChangeFilters({
                      ...filters,
                      time_range: value.time_range || '7d',
                      start_time: undefined,
                      end_time: undefined,
                    });
                    setDates([null, null]);
                  }}
                />
              )}
              <div style={{ display: 'flex', justifyContent: 'center' }}>
                <CustomRangePicker
                  dates={dates}
                  setDates={setDates}
                  onClear={onDateRangeClear}
                  format="YYYY-MM-DD"
                  onChange={onChangeDateRangeFilter}
                  className={
                    filters.start_time !== undefined && filters.end_time !== undefined
                      ? styles.activeDatePicker
                      : ''
                  }
                  value={
                    filters.start_time && filters.end_time
                      ? [dayjs(filters.start_time), dayjs(filters.end_time)]
                      : [undefined, undefined]
                  }
                />
              </div>
            </Flex>
          </Flex>

          <Flex className={styles.dropdownContainer} gap={10}>
            <Dropdown
              className={clsx(styles.dropdown, styles.conversationTypeDropdown)}
              wrapperClassName={styles.wrapperClassName}
              dropdownListClass={styles.typeDropdownList}
              onChange={handleConversationTypeChange}
              placeholder={t('surveys:fields.conversationType')}
              options={conversationTypeOptions}
              value={filters.conversation_type}
            />

            <Dropdown
              wrapperClassName={clsx(styles.wrapperClassName, styles.desktopClassName)}
              className={styles.dropdown}
              dropdownListClass={styles.desktopDropdownList}
              onChange={handleScoreChange}
              placeholder={t('surveys:fields.score')}
              options={scoreOptions}
              value={filters.status}
            />
            <Dropdown
              wrapperClassName={styles.wrapperClassName}
              className={styles.dropdown}
              dropdownListClass={styles.scoreDropdownList}
              onChange={handleScoreTypeChange}
              placeholder={t('surveys:fields.scoreType')}
              options={scoreTypeOptions}
              value={filters.score_type}
            />
            <Dropdown
              wrapperClassName={clsx(styles.wrapperClassName, styles.desktopClassName)}
              className={styles.dropdown}
              dropdownListClass={styles.scoreDropdownList}
              onChange={handleSurveyCategoryChange}
              placeholder={t('surveys:fields.category')}
              options={surveyCategoryOptions}
              value={filters.survey_category}
            />

            {isFetched && (
              <CustomFilters
                customClassName={styles.customSelectClassName}
                companiesList={companiesList}
                filters={filters}
                defaultCompanyValue={undefined}
                onChangeFilters={(updatedFilters) => {
                  onChangeFilters({
                    ...filters,
                    company_name: updatedFilters.company_name,
                    topic: undefined,
                  });
                }}
                onSearchQueryChange={onSearchQueryChange}
                searchResults={searchResults}
                isSearchLoading={isSearchLoading}
                searchError={searchError}
              />
            )}
          </Flex>
        </Flex>

        <Flex
          gap={20}
          className={clsx(
            styles.bottomHeaderContainer,
            surveysNumber == 0 && styles.searchSectionContainerWithoutScore
          )}
          wrap="wrap"
          justify="space-between"
          align="center"
        >
          {surveysNumber > 0 && (
            <Card className={styles.averageScore}>
              <Flex vertical>
                <p className={styles.averageTitle}>
                  {t('surveys:header.averageScore.title')}
                  <Tooltip title={t('surveys:header.averageScore.hint')}>
                    <Icon
                      component={() => <InfoCircle fill="none" height="18" width="18" />}
                      className={styles.averageScoreInfoIcon}
                    />
                  </Tooltip>
                </p>
                <span className={styles.averageSubtitle}>
                  {t('surveys:header.averageScore.subtitle')}
                </span>
              </Flex>

              <div>
                <Score score={averageScore} />
              </div>
            </Card>
          )}

          <Flex
            justify="space-between"
            gap={20}
            className={clsx(
              styles.searchSectionContainer,
              surveysNumber == 0 && styles.searchSectionContainerWithoutScore
            )}
          >
            <Flex vertical gap={5} className={styles.searchContainer}>
              <Input
                onChange={(event) => onQueryChange(event.target.value)}
                value={filterValue}
                placeholder={t('common:actions.search')}
                prefix={
                  <Search stroke={styles.iconColor} height={16} width={16} strokeWidth={2.5} />
                }
              />

              <Flex gap={5}>
                <Button
                  rightIcon
                  onClick={onExportHistory}
                  icon={<Download1 stroke={styles.iconColor} style={{ width: 10, height: 10 }} />}
                  style={{
                    fontSize: 10,
                    fontWeight: 400,
                    color: styles.buttonColor,
                    padding: isMobile ? '0px 5px' : '0px 12px',
                  }}
                  buttonVariant="tertiary"
                >
                  <Tooltip title={t('surveys:header.exportHint')}>
                    {t('surveys:header.export')}
                  </Tooltip>
                </Button>
              </Flex>
            </Flex>
          </Flex>
        </Flex>
      </Flex>
    </Card>
  );
};
